import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocale } from "../contexts/LocaleContext";
import { fetchArtworks, randomArtworks, searchArtworks, type Artwork } from "../api/artworks";
import { frameImageUrl } from "../lib/frameImage";
import { StreetMap } from "../earth/StreetMap";
import { pixelIntegerFromLatLng } from "../earth/pixel";
import { useEscapeKey } from "../hooks/useEscapeKey";
import "./styles/art-search.css";

export function ArtSearchPage() {
  const { t } = useLocale();
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [random, setRandom] = useState<Artwork[]>([]);
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [selected, setSelected] = useState<Artwork | null>(null);
  const [focused, setFocused] = useState<Artwork | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const mapHost = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setSelected(null), []);
  const onReady = useCallback((instance: google.maps.Map | null) => setMap(instance), []);
  useEscapeKey(selected !== null, close);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(false);
    void fetchArtworks(controller.signal).then(rows => {
      setArtworks(rows); setRandom(randomArtworks(rows));
    }).catch(() => { if (!controller.signal.aborted) setError(true); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [attempt]);

  const shown = useMemo(() => query ? searchArtworks(artworks, query) : random, [artworks, query, random]);
  const choose = useCallback((artwork: Artwork) => {
    setFocused(artwork);
    if (map) { map.panTo(artwork.info); map.setZoom(12); }
    mapHost.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setSelected(artwork);
  }, [map]);
  useEffect(() => {
    if (!map) return;
    if (focused) { map.panTo(focused.info); map.setZoom(12); }
  }, [map, focused]);
  useEffect(() => {
    if (!map) return;
    const markers = artworks.map(artwork => {
      const marker = new google.maps.Marker({ map, position: artwork.info, title: artwork.frameName || artwork.artistName });
      const listener = marker.addListener("click", () => choose(artwork));
      return { marker, listener };
    });
    return () => markers.forEach(({ marker, listener }) => { listener.remove(); marker.setMap(null); });
  }, [map, artworks, choose]);

  useEffect(() => {
    if (!selected || !dialog.current) return;
    const previous = document.activeElement as HTMLElement | null;
    const bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current.focus();
    return () => { document.body.style.overflow = bodyOverflow; previous?.focus(); };
  }, [selected]);

  return <section className="wrapper artwork-search">
    <h1>{t("artSearch.title")}</h1>
    <p>{t("artSearch.intro")}</p>
    {!loading && !error && <p className="artwork-count">{t("artSearch.count", { count: artworks.length })}</p>}
    <form className="artwork-search-form" onSubmit={event => { event.preventDefault(); setQuery(input.trim()); }}>
      <label htmlFor="artwork-query">{t("artSearch.label")}</label>
      <div className="artwork-search-controls">
        <input id="artwork-query" type="search" value={input} onChange={event => setInput(event.target.value)} placeholder={t("artSearch.placeholder")} />
        <button type="submit" disabled={loading || error}>{t("artSearch.search")}</button>
        <button type="button" className="secondary" disabled={loading || error} onClick={() => { setInput(""); setQuery(""); setRandom(randomArtworks(artworks)); }}>{t("artSearch.random")}</button>
      </div>
    </form>
    {loading && <p role="status">{t("artSearch.loading")}</p>}
    {error && <div role="alert"><p>{t("artSearch.error")}</p><button onClick={() => setAttempt(value => value + 1)}>{t("artSearch.retry")}</button></div>}
    {!loading && !error && <>
      <p role="status">{query ? t("artSearch.results", { count: shown.length }) : t("artSearch.selection")}</p>
      {shown.length === 0 && <p>{t(query ? "artSearch.noResults" : "artSearch.empty")}</p>}
      <div className="artwork-grid">
        {shown.map(artwork => <button key={artwork.pixel} type="button" className="artwork-card" onClick={() => choose(artwork)}>
          <img src={frameImageUrl(artwork.image)} alt={artwork.frameName || t("world.artworkAlt")} loading="lazy" />
          <span className="artwork-card-title">{artwork.frameName || t("common.noTitle")}</span>
          <span>{artwork.artistName || t("common.unknown")}</span>
          <span className="artwork-card-place">{[artwork.location, artwork.country].filter(Boolean).join(" · ")}</span>
        </button>)}
      </div>
    </>}
    <div ref={mapHost} className="artwork-map">
      <h2>{t("artSearch.map")}</h2>
      <StreetMap center={{ lat: 20, lng: 0 }} range={20000000} onReady={onReady} selectedPixel={focused?.pixel ?? null}
        onSelect={point => { const pixel = pixelIntegerFromLatLng(point.lat, point.lng); const artwork = artworks.find(row => row.pixel === pixel); if (artwork) choose(artwork); }} />
    </div>
    {selected && <div className="popup-overlay artwork-popup" style={{ display: "flex" }} onClick={event => { if (event.target === event.currentTarget) close(); }}>
      <div ref={dialog} className="popup-content" role="dialog" aria-modal="true" aria-labelledby="artwork-dialog-title" tabIndex={-1}
        onKeyDown={event => { if (event.key === "Tab") { event.preventDefault(); dialog.current?.querySelector<HTMLButtonElement>("button")?.focus(); } }}>
        <h2 id="artwork-dialog-title">{selected.frameName || t("common.noTitle")}</h2>
        <div className="image-wrap"><img src={frameImageUrl(selected.image)} alt={selected.frameName || t("world.artworkAlt")} /></div>
        <div className="popup-info">
          <p><strong>{t("common.artist")}:</strong> {selected.artistName || t("common.unknown")}</p>
          <p><strong>{t("common.location")}:</strong> {selected.location || t("common.unknown")}</p>
          <p><strong>{t("common.country")}:</strong> {selected.country || t("common.unknown")}</p>
          <p><strong>{t("world.imageName")}</strong> {selected.frameName || t("common.noTitle")}</p>
        </div>
        <button type="button" className="btn-cancel secondary" onClick={close}>{t("common.close")}</button>
      </div>
    </div>}
  </section>;
}
